<template>
  <div class="pa-3 pt-0 d-flex flex-column body-2 subscription-card">
    <div
      v-if="loading"
      class="d-flex justify-center align-center"
      style="min-height: 157px"
    >
      <hollow-dots-spinner
        :animation-duration="1000"
        :dot-size="20"
        :dots-num="3"
        color="#099845"
      />
    </div>
    <div v-else>
      <template v-if="isSubscribed">
        <div class="d-flex align-start justify-space-around mb-3">
          <!-- Plano Principal - Lado Esquerdo -->
          <div class="main-plan">
            <div class="caption grey--text mb-1">Plano Principal</div>
            <div class="title font-weight-bold secondary--text mb-1">
              {{ currentSubscription.productTypeLabel }}
            </div>
          </div>

          <!-- Planos Addon - Lado Direito -->
          <div v-if="activeAddonSubscriptions.length > 0" class="addon-section">
            <div class="caption grey--text mb-1">Planos adicionais</div>
            <div class="addon-list-vertical">
              <v-chip
                v-for="addon in activeAddonSubscriptions"
                :key="addon.id"
                x-small
                color="primary"
                outlined
                class="mb-1 addon-chip"
              >
                <v-icon left x-small>mdi-map-marker-path</v-icon>
                {{ addon.name }}
              </v-chip>
            </div>
          </div>
        </div>

        <!-- Informações de Data - Centralizado -->
        <div class="text-center">
          <div
            v-if="isStatusCancelled"
            class="d-flex align-center justify-center"
          >
            <span class="caption cancelled-text mr-2">
              Acesso garantido até
              {{ currentSubscription.nextBillingTime || "data não disponível" }}
            </span>
            <v-chip
              x-small
              color="orange"
              text-color="white"
              class="cancelled-chip"
            >
              <v-icon left x-small>mdi-pause-circle-outline</v-icon>
              CANCELADO
            </v-chip>
          </div>
          <div
            v-else-if="currentSubscription.nextBillingTime && isStatusActive"
            class="caption grey--text mt-1 font-weight-medium"
          >
            {{
              currentSubscription.status === "TRIALING"
                ? "Em avaliação gratuita até"
                : "Próxima renovação em"
            }}
            {{ currentSubscription.nextBillingTime }}
          </div>
        </div>
      </template>
      <template v-else>
        <div class="text-center">
          <div class="mb-2">Nenhum plano ativado.</div>

          <!-- Animação de Call-to-Action para não assinantes -->
          <div class="cta-animation">
            <v-icon color="primary" size="32" class="pulse-icon mb-1">
              mdi-rocket-launch
            </v-icon>
            <div class="caption text--primary font-weight-medium">
              Desbloqueie todo o potencial da plataforma!
            </div>
          </div>
        </div>
      </template>
      <v-btn
        rounded
        small
        :color="
          isStatusCancelled ? 'orange' : isSubscribed ? 'primary' : 'success'
        "
        class="mt-4"
        :class="{ 'bounce-btn': !isSubscribed }"
        @click="
          $router.push({
            name: `subscriptions.${!isSubscribed ? 'plans' : 'my'}`,
          })
        "
      >
        <v-icon left>
          {{
            isStatusCancelled
              ? "mdi-refresh"
              : isSubscribed
              ? "mdi-cog"
              : "mdi-star"
          }}
        </v-icon>
        <span>{{ labelButton }}</span>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { getAddonSubscriptions } from "@/services/external/paypal-service";
import { HollowDotsSpinner } from "epic-spinners";

export default {
  name: "SubscriptionCard",
  components: {
    HollowDotsSpinner,
  },
  props: {
    currentSubscription: {
      type: Object,
      default: () => ({
        nextBillingTime: "",
        productTypeLabel: "",
      }),
    },
    isLoadingSubscription: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      addonSubscriptions: [],
      isLoadingAddons: true,
    };
  },
  computed: {
    loading() {
      return (
        this.isLoadingSubscription ||
        (this.isSubscribed && this.isLoadingAddons)
      );
    },
    isSubscribed() {
      return !!this.currentSubscription.status;
    },
    isStatusActive() {
      return (
        this.currentSubscription.status === "ACTIVE" ||
        this.currentSubscription.status === "TRIALING"
      );
    },
    isStatusCancelled() {
      return this.currentSubscription.status === "CANCELED";
    },
    activeAddonSubscriptions() {
      return this.addonSubscriptions.filter(
        (addon) => addon.status === "ACTIVE" || addon.status === "TRIALING"
      );
    },
    labelButton() {
      if (this.isStatusActive) {
        return "Gerenciar minhas assinaturas";
      } else if (this.isStatusCancelled) {
        return "Reativar assinatura";
      } else {
        return "Contratar plano";
      }
    },
  },
  watch: {
    isSubscribed: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.isLoadingAddons = true;
          this.fetchAddonSubscriptions();
        } else {
          this.isLoadingAddons = false;
        }
      },
    },
  },
  mounted() {
    if (this.isSubscribed) {
      this.fetchAddonSubscriptions();
    } else {
      this.isLoadingAddons = false;
    }
  },
  methods: {
    async fetchAddonSubscriptions() {
      if (!this.isSubscribed) return;

      try {
        const response = await getAddonSubscriptions();
        this.addonSubscriptions = response.data || [];
      } catch (error) {
        console.error("Erro ao buscar addons:", error);
        this.addonSubscriptions = [];
      } finally {
        this.isLoadingAddons = false;
      }
    },
  },
};
</script>

<style scoped>
.subscription-card {
  justify-content: space-between;
  margin-top: -25px;
  min-height: 157px;
  position: relative;
}

.addon-list {
  width: 100%;
  max-height: 80px;
  overflow-y: auto;
}

/* Estilos para a nova disposição */
.main-plan {
  min-width: 0; /* Permite que o flex-grow funcione corretamente */
}

.addon-section {
  max-width: 180px;
  min-width: 140px;
  text-align: left;
}

.addon-list-vertical {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-height: 100px;
  overflow-y: auto;
}

.addon-chip {
  width: 100%;
  justify-content: flex-start;
}

/* Animação de pulsação para o ícone */
.pulse-icon {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Animação de bouncing para o botão */
.bounce-btn {
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-3px);
  }
  60% {
    transform: translateY(-2px);
  }
}

/* Estilo discreto para status cancelado */
.cancelled-chip {
  opacity: 0.9;
}

.cancelled-text {
  color: #ff9800;
  font-style: italic;
}

/* Efeito hover para o call-to-action */
.cta-animation {
  transition: transform 0.3s ease;
}

.cta-animation:hover {
  transform: translateY(-2px);
}

/* Estilo para os chips de addon */
.v-chip.v-size--small {
  font-size: 0.75rem;
}
</style>
